import type { McpOAuthBlocker, McpOAuthOverCap, McpOAuthRequest, McpOAuthSession } from "../api/mcp";

// Presentation rules for MCP client sign-in with OAuth: the consent page
// (`/mcp/authorize`), the Connect a client panel, and Connected apps on the Seats tab.
// Kept free of Svelte so they are unit-testable.

export const MCP_AUTHORIZE_PATH = "/mcp/authorize";
/** The name every command registers the server under in the client. */
export const MCP_CLIENT_SERVER_NAME = "epidev";
export const MCP_PUBLIC_URL_PLACEHOLDER = "<your-server-url>";
export const MCP_CODEX_LOGIN_COMMAND = `codex mcp login ${MCP_CLIENT_SERVER_NAME}`;
/** How a person restarts a sign-in from their app, used wherever a request is gone. */
export const MCP_RESTART_SIGN_IN = "Start the sign-in again from your app, for example run /mcp in Claude Code or codex mcp login epidev in Codex.";

/** The API's request id shape (`oar_` + 22 url-safe characters). */
const REQUEST_ID = /^oar_[A-Za-z0-9_-]{22}$/;

/** The `request` query parameter when it can be a request id, else null. */
export function mcpOAuthRequestId(value: string | null | undefined): string | null {
  const trimmed = (value ?? "").trim();
  return REQUEST_ID.test(trimmed) ? trimmed : null;
}

// ---- public URL ------------------------------------------------------------------------

const LOOPBACK_HOSTS = new Set(["127.0.0.1", "[::1]", "localhost"]);

export type McpPublicUrlResult = { ok: true; value: string | null } | { ok: false; error: string };

/**
 * Validates and normalizes what an admin types as the server's public URL, with the
 * API's rules: an https origin (`https://host[:port]`), or http on 127.0.0.1, [::1], or
 * localhost; no credentials, path, query, or fragment. One trailing slash is dropped.
 * An empty value clears the URL (`value: null`).
 */
export function normalizeMcpPublicUrl(raw: string): McpPublicUrlResult {
  const trimmed = raw.trim();
  if (!trimmed) return { ok: true, value: null };
  const input = trimmed.endsWith("/") && !trimmed.endsWith("//") ? trimmed.slice(0, -1) : trimmed;
  let url: URL;
  try {
    url = new URL(input);
  } catch {
    return { ok: false, error: "Enter a full URL, for example https://mcp.example.com." };
  }
  if (!/^https?:\/\//i.test(input)) return { ok: false, error: "Use an https:// URL, for example https://mcp.example.com." };
  const loopback = LOOPBACK_HOSTS.has(url.hostname.toLowerCase());
  if (url.protocol === "http:" && !loopback) {
    return { ok: false, error: "Use https. Plain http is allowed only on 127.0.0.1, [::1], or localhost." };
  }
  if (url.username || url.password) return { ok: false, error: "Leave out the user name and password." };
  if (input.includes("?") || input.includes("#")) return { ok: false, error: "Leave out the query and fragment: enter the origin only, for example https://mcp.example.com." };
  if (/^\/hosted\/h_[a-f0-9]{24}$/.test(url.pathname)) return { ok: true, value: `${url.origin}${url.pathname}` };
  if (url.pathname !== "/" || /^https?:\/\/[^/]+\//i.test(input)) {
    const hint = url.pathname.replace(/\/+$/, "").toLowerCase().endsWith("/mcp") ? " without /mcp" : "";
    return { ok: false, error: `Enter the origin only${hint}, for example ${url.origin}.` };
  }
  return { ok: true, value: url.origin };
}

/** `<publicUrl>/mcp`, or the placeholder form while no public URL is saved. */
export function mcpEndpointUrl(publicUrl: string | null | undefined): string {
  return `${publicUrl ? publicUrl.replace(/\/+$/, "") : MCP_PUBLIC_URL_PLACEHOLDER}/mcp`;
}

export function mcpClaudeCodeCommand(publicUrl: string | null | undefined): string {
  return `claude mcp add --transport http ${MCP_CLIENT_SERVER_NAME} ${mcpEndpointUrl(publicUrl)}`;
}

export function mcpCodexCommand(publicUrl: string | null | undefined): string {
  return `codex mcp add ${MCP_CLIENT_SERVER_NAME} --url ${mcpEndpointUrl(publicUrl)}`;
}

// ---- consent page ----------------------------------------------------------------------

/** Where the browser goes after a decision, said plainly (the AS must show the redirect host). */
export function mcpOAuthRedirectLabel(client: Pick<McpOAuthRequest["client"], "redirectHost" | "redirectLoopback">): string {
  return client.redirectLoopback ? `An app on this computer (${client.redirectHost})` : client.redirectHost;
}

const SCOPE_COPY: Record<string, string> = {
  mcp: "Use this MCP server's tools as you, within your MCP permissions"
};

export function mcpOAuthScopeLabel(scope: string): string {
  return SCOPE_COPY[scope] ?? scope;
}

export interface McpOAuthBlockerCopy {
  tone: "warn" | "error";
  title: string;
  detail: string;
  /** A portal tab that fixes it, offered to people who can act on it. */
  link: { href: string; label: string } | null;
}

/** Why Approve is unavailable, with the next step for this person. */
export function mcpOAuthBlockerCopy(
  blocker: McpOAuthBlocker,
  context: { canManage: boolean; orgId: string; orgName: string; serverName: string }
): McpOAuthBlockerCopy {
  const tab = (id: string) => `/orgs/${encodeURIComponent(context.orgId)}/mcp?tab=${id}`;
  switch (blocker) {
    case "mcp_seat_required":
      return context.canManage
        ? {
            tone: "warn",
            title: "You need an MCP seat",
            detail: `Signing in to ${context.serverName} needs an MCP seat, and you don't hold one in ${context.orgName}. Assign yourself an MCP seat on the Seats tab, then approve.`,
            link: { href: tab("seats"), label: "Open the Seats tab" }
          }
        : {
            tone: "warn",
            title: "You need an MCP seat",
            detail: `Signing in to ${context.serverName} needs an MCP seat. Ask an owner or admin of ${context.orgName} to assign you an MCP seat, then approve.`,
            link: null
          };
    case "mcp_policy_required":
      return {
        tone: "warn",
        title: "No MCP permissions are published yet",
        detail: context.canManage
          ? `${context.serverName} has no published permissions, so it cannot accept sign-ins. Publish a policy on the Permissions tab, then approve.`
          : `${context.serverName} has no published permissions, so it cannot accept sign-ins. Ask an owner or admin of ${context.orgName} to publish a policy on the Permissions tab.`,
        link: context.canManage ? { href: tab("permissions"), label: "Open the Permissions tab" } : null
      };
    case "mcp_server_revoked":
      return {
        tone: "error",
        title: "This server was unlinked",
        detail: `${context.serverName} is no longer linked to ${context.orgName}, so it cannot accept sign-ins. Deny this request, then connect your app to a linked server.`,
        link: null
      };
  }
}

/** The blocker an approve refusal reveals, when it is one the page can explain in place. */
export function mcpOAuthBlockerFromCode(code: string): McpOAuthBlocker | null {
  return code === "mcp_seat_required" || code === "mcp_policy_required" || code === "mcp_server_revoked" ? code : null;
}

export type McpOAuthRequestGone = "not_found" | "expired";

export function mcpOAuthRequestGone(error: { code: string; status: number }): McpOAuthRequestGone | null {
  if (error.code === "mcp_oauth_request_expired" || error.status === 410) return "expired";
  if (error.code === "mcp_oauth_request_not_found" || error.status === 404) return "not_found";
  return null;
}

// ---- connected apps --------------------------------------------------------------------

/** When the sign-in was last used; a sign-in never used since it was created falls back to that. */
export function mcpOAuthSessionLastSeen(session: Pick<McpOAuthSession, "lastSeenAt" | "createdAt">): string {
  return session.lastSeenAt ?? session.createdAt;
}

export function mcpOAuthSessionIdLabel(session: Pick<McpOAuthSession, "idSuffix">): string {
  return `…${session.idSuffix}`;
}

export function mcpOAuthOverCapLabel(activeSessions: number, sessionCap: number): string {
  return `${activeSessions} sign-ins (over ${sessionCap})`;
}

export interface McpOAuthSessionGroup {
  userId: string;
  name: string;
  email: string;
  sessions: McpOAuthSession[];
  /** (user, server) pairs above the soft cap, with the server's name when known. */
  overCap: Array<McpOAuthOverCap & { serverName: string }>;
}

/**
 * Active sign-ins grouped by person (sorted by name), each group newest-used first.
 * Revoked rows are dropped defensively; the list is of active sign-ins.
 */
export function groupMcpOAuthSessions(sessions: readonly McpOAuthSession[], overCap: readonly McpOAuthOverCap[] = []): McpOAuthSessionGroup[] {
  const groups = new Map<string, McpOAuthSessionGroup>();
  const serverNames = new Map<string, string>();
  for (const session of sessions) {
    serverNames.set(session.serverId, session.serverName);
    if (session.revokedAt) continue;
    let group = groups.get(session.userId);
    if (!group) {
      group = { userId: session.userId, name: session.displayName ?? session.email, email: session.email, sessions: [], overCap: [] };
      groups.set(session.userId, group);
    }
    group.sessions.push(session);
  }
  for (const entry of overCap) {
    const group = groups.get(entry.userId);
    if (group) group.overCap.push({ ...entry, serverName: serverNames.get(entry.serverId) ?? entry.serverId });
  }
  const seen = (session: McpOAuthSession) => Date.parse(mcpOAuthSessionLastSeen(session));
  return [...groups.values()]
    .map((group) => ({ ...group, sessions: [...group.sessions].sort((a, b) => seen(b) - seen(a)) }))
    .sort((a, b) => a.name.localeCompare(b.name) || a.email.localeCompare(b.email));
}

/** True when the list spans more than one server, so each row names its server. */
export function mcpOAuthSpansServers(sessions: readonly Pick<McpOAuthSession, "serverId">[]): boolean {
  return new Set(sessions.map((session) => session.serverId)).size > 1;
}
